<template>
    <div>
        <template v-if="!isEmbed">
            <!-- Root catalogs do not have their own title, so keep one page-level heading available for SEO and assistive tech. -->
            <h1
                v-if="root"
                class="sr-only"
            >
                {{ activeFacility.title }}
            </h1>

            <h1
                v-else
                class="mb-3 text-2xl font-semibold tracking-wider"
            >
                {{ catalog.title }}
            </h1>
            <content-reset :body="catalog.body" />
        </template>

        <div class="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <template
                v-for="child in children"
                :key="child.child.slug"
            >
                <nuxt-link
                    v-if="child.intercept !== undefined"
                    custom
                    :to="child.route"
                >
                    <a
                        :href="child.route"
                        @click.prevent="handleClick($event, child.intercept)"
                    >
                        <storefront-catalog-child :child="child.child" />
                    </a>
                </nuxt-link>

                <nuxt-link
                    v-else
                    :to="child.route"
                    :target="child.target"
                >
                    <storefront-catalog-child :child="child.child" />
                </nuxt-link>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { StorefrontCatalogScalar } from '~/resources/scalars/storefront-catalog-scalar'
import { computed } from 'vue'
import NuxtLink from '#app/components/nuxt-link'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useHead } from '@unhead/vue'
import {
    layoutModeKioskLink,
    layoutModeLightboxLink,
    unsafe_facilityCreateContractLink,
    useRouteLink
} from '~/composables/routing'
import { useNuxtApp } from '#app'
import { useEmbedParams } from '~/composables/use-embed-params'
import StorefrontCatalogChild from '~/components/Storefront/StorefrontCatalogChild.vue'
import { getRequestHost } from 'h3'
import type { PlanType } from '~/resources/graphql/graphql'
import { useLayoutMode } from '~/composables/use-layout-mode'
import ContentReset from '~/components/ContentReset.vue'
import { useStorefrontCatalogCalendarSeo } from '~/composables/use-storefront-catalog-calendar-seo'

const props = withDefaults(
    defineProps<{
        catalog: StorefrontCatalogScalar
        storefrontPrefix: string
        root?: boolean
    }>(),
    {
        root: false
    }
)

const activeFacility = useActiveFacility()

// The root catalog is rendered directly by the storefront index page, so it
// owns the browser title here instead of going through StorefrontNode.
if (props.root) {
    useHead({
        title: activeFacility.value.title
    })
}

const catalogSeoNode = computed(() => {
    // Non-root catalog pages are handled by StorefrontNode, while the root
    // storefront page renders this component directly.
    return props.root ? props.catalog : null
})
useStorefrontCatalogCalendarSeo(catalogSeoNode)

const nuxt = useNuxtApp()
const locationOrigin = computed(() => {
    if (process.server && nuxt.ssrContext) {
        return getRequestHost(nuxt.ssrContext.event)
    } else if (location !== undefined) {
        return location.origin
    } else {
        return ''
    }
})

const embedParams = useEmbedParams()
const { kioskSlug } = useLayoutMode()
function makeContractLink(planType: PlanType, slug: string): string {
    if (kioskSlug.value !== null) {
        return layoutModeKioskLink(
            kioskSlug.value,
            unsafe_facilityCreateContractLink(
                props.storefrontPrefix,
                planType,
                slug
            )
        )
    } else {
        return facilityCreateContractLink(
            props.storefrontPrefix,
            planType,
            slug
        )
    }
}

const { storefrontNodeLink, facilityCreateContractLink } = useRouteLink()
const isEmbed = embedParams !== null
const children = computed(() => {
    return props.catalog.children.map(child => {
        if (child.__typename === 'StorefrontPlan') {
            return {
                child,
                target:
                    embedParams !== null && embedParams.mode === 'new-tab'
                        ? '_blank'
                        : undefined,
                route: makeContractLink(child.planType, child.slug),
                intercept:
                    embedParams !== null && embedParams.mode === 'same-tab'
                        ? {
                              key: 'parent-redirect',
                              url:
                                  locationOrigin.value +
                                  unsafe_facilityCreateContractLink(
                                      props.storefrontPrefix,
                                      child.planType,
                                      child.slug
                                  )
                          }
                        : embedParams !== null &&
                            embedParams.mode === 'lightbox'
                          ? {
                                key: 'lightbox',
                                backgroundColor:
                                    activeFacility.value.branding.primaryColor,
                                url:
                                    locationOrigin.value +
                                    layoutModeLightboxLink(
                                        unsafe_facilityCreateContractLink(
                                            props.storefrontPrefix,
                                            child.planType,
                                            child.slug
                                        )
                                    )
                            }
                          : undefined
            }
        }

        return {
            child,
            target: undefined,
            route: isEmbed
                ? `${props.storefrontPrefix}/embed/${child.slug}`
                : storefrontNodeLink(props.storefrontPrefix, child.slug)
        }
    })
})

function handleClick(event: PointerEvent, message: Record<string, string>) {
    parent.postMessage('RPHQ::1::' + JSON.stringify(message), '*')
}
</script>
